/**
 * Keeps a list's place by the row at its top, not by pixels: the mail list is rebuilt on a layout change (the split
 * collapsing, the narrow toolbar, Back from a thread) and its rows change height on the way (one line wide, stacked
 * narrow), so it comes back with the same mail at the top, as far down as it was. Kept per list (`key`: folder, tab,
 * search, filters) for the session; a list scrolled back to the top keeps nothing.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

type Anchor = { id: string; offset: number };
type Row = (list: HTMLElement, id: string) => Element | null;

const anchors = new Map<string, Anchor>();

/** The first row that still shows under the list's top edge, and how far its top sits from that edge. */
function topRow(list: HTMLElement, ids: readonly string[], row: Row): Anchor | undefined {
  const top = list.getBoundingClientRect().top;
  for (const id of ids) {
    const r = row(list, id)?.getBoundingClientRect();
    if (r !== undefined && r.bottom > top + 1) return { id, offset: r.top - top };
  }
  return undefined;
}

/** Scrolls `list` so the anchored row sits where it did. */
function settle(list: HTMLElement, a: Anchor, row: Row): void {
  const target = row(list, a.id);
  if (target !== null) list.scrollBy({ top: target.getBoundingClientRect().top - list.getBoundingClientRect().top - a.offset, behavior: "instant" });
}

export function useListAnchor(key: string, list: RefObject<HTMLDivElement | null>, ids: readonly string[], row: Row): void {
  const idsRef = useRef(ids);
  useEffect(() => {
    idsRef.current = ids;
  }, [ids]);
  const shown = ids.length > 0;
  // The scroller this follows: a new one (the layout swapped it) or another list settles on the anchor first.
  const attached = useRef<{ el: HTMLElement; key: string; detach: () => void } | undefined>(undefined);
  useLayoutEffect(() => {
    const el = list.current;
    const now = attached.current;
    if (now !== undefined && now.el === el && now.key === key) return;
    now?.detach();
    attached.current = undefined;
    if (el === null || !shown) return;
    // Until I scroll or press a key, the list stays on the kept row while its rows settle (they animate in, restack).
    let pinned = anchors.has(key);
    const settleKept = () => {
      const a = anchors.get(key);
      if (a !== undefined) settle(el, a, row);
    };
    if (pinned) settleKept();
    const unpin = () => {
      pinned = false;
    };
    // Scrolling moves the anchor (at most every 100 ms), but not while the list is changing width.
    let width = el.clientWidth;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const save = () => {
      timer = undefined;
      if (pinned || el.clientWidth !== width) return;
      const a = el.scrollTop < 1 ? undefined : topRow(el, idsRef.current, row);
      if (a === undefined) anchors.delete(key);
      else anchors.set(key, a);
    };
    const onScroll = () => {
      timer ??= setTimeout(save, 100);
    };
    const opts = { passive: true } as const;
    el.addEventListener("scroll", onScroll, opts);
    for (const e of ["wheel", "touchstart", "pointerdown"] as const) el.addEventListener(e, unpin, opts);
    window.addEventListener("keydown", unpin, opts);
    const ro =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(() => {
            // The list or its rows changed size (a new width, rows restacking a render later, mail arriving above):
            // the anchored row stays put. Only my scrolling moves the anchor.
            width = el.clientWidth;
            settleKept();
          });
    ro?.observe(el);
    for (const child of el.children) ro?.observe(child);
    attached.current = {
      el,
      key,
      detach: () => {
        ro?.disconnect();
        el.removeEventListener("scroll", onScroll);
        for (const e of ["wheel", "touchstart", "pointerdown"] as const) el.removeEventListener(e, unpin);
        window.removeEventListener("keydown", unpin);
        if (timer !== undefined) clearTimeout(timer);
      },
    };
  });
  useEffect(
    () => () => {
      attached.current?.detach();
      attached.current = undefined;
    },
    [],
  );
}
